import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Boxes, Pencil, X, BarChart3, Search, RefreshCw, Tag, Sparkles } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { ProdottoStatsModal } from "../components/StatsModal";

const CAT_STYLE = {
  Frutta: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  Verdura: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Agrumi: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Erbe aromatiche": "bg-lime-500/15 text-lime-300 border-lime-500/30",
  Altro: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

export default function Prodotti() {
  const { text } = useSettings();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ nome: "", categoria: "", unita: "colli" });
  const [editId, setEditId] = useState(null);
  const [statsId, setStatsId] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [reindexing, setReindexing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/prodotti");
    setItems(data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const startEdit = (p) => { setEditId(p.id); setForm({ nome: p.nome, categoria: p.categoria || "", unita: p.unita || "colli" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancelEdit = () => { setEditId(null); setForm({ nome: "", categoria: "", unita: "colli" }); };

  const create = async (e) => {
    e.preventDefault();
    if (!form.nome) { toast.error("Il nome del prodotto è obbligatorio"); return; }
    try {
      if (editId) { await api.put(`/prodotti/${editId}`, form); toast.success("Prodotto aggiornato"); }
      else { await api.post("/prodotti", form); toast.success("Prodotto aggiunto"); }
      cancelEdit();
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/prodotti/${id}`); toast.success("Eliminato"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const reindex = async () => {
    setReindexing(true);
    try { const { data } = await api.post("/prodotti/reindex"); toast.success(`Catalogo riordinato (${data.prodotti} articoli)`); await load(); }
    catch (err) { toast.error(apiError(err)); }
    setReindexing(false);
  };

  const categorie = useMemo(() => {
    const set = {};
    items.forEach((p) => { const c = p.categoria || "Altro"; set[c] = (set[c] || 0) + 1; });
    return Object.entries(set).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((p) => {
      if (cat && (p.categoria || "Altro") !== cat) return false;
      if (!ql) return true;
      const inName = (p.nome || "").toLowerCase().includes(ql);
      const inForn = (p.fornitori || []).some((f) => (f?.nome || "").toLowerCase().includes(ql));
      return inName || inForn;
    });
  }, [items, q, cat]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((p) => { const c = p.categoria || "Altro"; (g[c] = g[c] || []).push(p); });
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length);
  }, [filtered]);

  const input = "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-primary";

  const Card = (p) => {
    const tags = p.fornitori || [];
    return (
      <div key={p.id} data-testid={`prodotto-${p.id}`} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 hover:border-slate-700 transition-colors">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-500/15 flex items-center justify-center shrink-0">
            <Boxes className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setStatsId(p.id)}>
            <div className="font-semibold text-slate-100 truncate hover:text-primary transition-colors">{p.nome}</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${CAT_STYLE[p.categoria] || CAT_STYLE.Altro}`}>{p.categoria || "Altro"}</span>
              <span className="text-[10px] text-slate-500">· {p.unita}</span>
              {p.auto && <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500"><Sparkles className="w-2.5 h-2.5" /> auto</span>}
            </div>
          </div>
          {p.ultimo_prezzo != null && (
            <div className="text-right shrink-0">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">ultimo</div>
              <div className="font-mono-plex text-emerald-400 text-sm">€ {p.ultimo_prezzo.toFixed(2)}</div>
            </div>
          )}
        </div>
        {tags.length > 0 && (
          <div className="flex items-center gap-1 mt-3 flex-wrap" data-testid={`prodotto-tags-${p.id}`}>
            <Tag className="w-3 h-3 text-slate-500" />
            {tags.slice(0, 4).map((f, i) => (
              <button key={f.nome || i} onClick={() => setQ(f.nome)} className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/25 hover:bg-purple-500/25 transition-colors truncate max-w-[130px]">
                {f.nome}
              </button>
            ))}
            {tags.length > 4 && <span className="text-[10px] text-slate-500">+{tags.length - 4}</span>}
          </div>
        )}
        <div className="flex items-center justify-end gap-1 mt-2">
          <button data-testid={`stats-prodotto-${p.id}`} onClick={() => setStatsId(p.id)} className="p-1.5 text-slate-500 hover:text-primary" title="Statistiche"><BarChart3 className="w-4 h-4" /></button>
          <button data-testid={`edit-prodotto-${p.id}`} onClick={() => startEdit(p)} className="p-1.5 text-slate-500 hover:text-primary"><Pencil className="w-4 h-4" /></button>
          <button data-testid={`delete-prodotto-${p.id}`} onClick={() => remove(p.id)} className="p-1.5 text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    );
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight">{text("prodotti_title")}</h1>
        <button data-testid="reindex-btn" onClick={reindex} disabled={reindexing} className="mt-1 flex items-center gap-2 text-sm bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-60">
          {reindexing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Riordina catalogo
        </button>
      </div>
      <p className="text-slate-400 mb-6">{text("prodotti_subtitle")}</p>

      <form onSubmit={create} data-testid="prodotto-form" className="bg-slate-900/50 border border-slate-800 rounded-md p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input data-testid="prodotto-nome" placeholder="Nome articolo" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className={input} />
        <input data-testid="prodotto-categoria" placeholder="Categoria (auto se vuoto)" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className={input} />
        <select value={form.unita} onChange={(e) => setForm({ ...form, unita: e.target.value })} className={input}>
          <option value="colli">colli</option>
          <option value="cassette">cassette</option>
          <option value="kg">kg</option>
          <option value="bancali">bancali</option>
        </select>
        <button data-testid="prodotto-submit" type="submit" className="bg-primary text-slate-950 font-semibold rounded-md px-4 py-2 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
          {editId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editId ? "Aggiorna" : "Aggiungi"}
        </button>
        {editId && <button type="button" data-testid="prodotto-cancel" onClick={cancelEdit} className="md:col-span-4 text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1"><X className="w-3 h-3" /> Annulla modifica</button>}
      </form>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="prodotti-search" placeholder="Cerca articolo o fornitore…" value={q} onChange={(e) => setQ(e.target.value)} className={`${input} pl-9`} />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="categoria-filtri">
          <button data-testid="categoria-filter-tutte" onClick={() => setCat("")} className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${!cat ? "bg-primary/20 text-primary border-primary/40" : "bg-slate-800 text-slate-400 border-slate-700"}`}>Tutte ({items.length})</button>
          {categorie.map(([c, n]) => (
            <button key={c} data-testid={`categoria-filter-${c}`} onClick={() => setCat(c === cat ? "" : c)} className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${cat === c ? "bg-primary/20 text-primary border-primary/40" : "bg-slate-800 text-slate-400 border-slate-700"}`}>{c} ({n})</button>
          ))}
        </div>
      </div>

      <div data-testid="prodotti-list" className="space-y-6">
        {filtered.length === 0 && <p className="text-slate-500 text-sm">Nessun articolo trovato.</p>}
        {grouped.map(([c, list]) => (
          <div key={c} data-testid={`categoria-gruppo-${c}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs uppercase tracking-[0.2em] px-2 py-0.5 rounded border ${CAT_STYLE[c] || CAT_STYLE.Altro}`}>{c}</span>
              <span className="text-xs text-slate-500">{list.length} articoli</span>
              <div className="flex-1 h-px bg-slate-800" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((p) => Card(p))}
            </div>
          </div>
        ))}
      </div>

      {statsId && <ProdottoStatsModal id={statsId} onClose={() => setStatsId(null)} />}
    </div>
  );
}
